import { Children, useLayoutEffect, useRef, useState } from 'react';
import { useWindowDimensions } from 'react-native';
import { useUnistyles } from 'react-native-unistyles';

function MasonryItem({ children, gap }) {
    const itemRef = useRef(null);
    const contentRef = useRef(null);

    useLayoutEffect(() => {
        const item = itemRef.current;
        const content = contentRef.current;
        // Measure natural content, never the grid-sized wrapper (avoids resize feedback).
        const measure = () => {
            item.style.gridRowEnd = `span ${Math.ceil(content.getBoundingClientRect().height + gap)}`;
        };
        const observer = new ResizeObserver(measure);
        observer.observe(content);
        measure();
        return () => observer.disconnect();
    }, [gap]);

    return (
        <div ref={itemRef} style={{ boxSizing: 'border-box', minWidth: 0, alignSelf: 'start' }}>
            <div ref={contentRef} style={{ boxSizing: 'border-box', display: 'flow-root' }}>{children}</div>
        </div>
    );
}

export default function DashboardGrid({ children }) {
    const { width: windowWidth } = useWindowDimensions();
    const { theme: { spacing } } = useUnistyles();
    const gridRef = useRef(null);
    // Measure the grid itself — on web the persistent side nav shrinks the
    // content area, so window width overstates available space.
    const [containerWidth, setContainerWidth] = useState(null);

    useLayoutEffect(() => {
        const el = gridRef.current;
        if (!el) return;
        const observer = new ResizeObserver((entries) => {
            setContainerWidth(entries[0].contentRect.width);
        });
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    const width = containerWidth ?? windowWidth;
    const paddingInline = width >= 768 ? spacing.lg : spacing.md;
    // Content-box divs aren't covered by RNW's per-component border-box, so
    // subtract padding before deciding how many columns fit.
    const innerWidth = width - paddingInline * 2;

    return (
        <div ref={gridRef} data-testid="dashboard-cards" style={{
            boxSizing: 'border-box',
            width: '100%',
            maxWidth: 1200,
            marginInline: 'auto',
            display: 'grid',
            gridTemplateColumns: innerWidth >= 900 ? 'repeat(2, minmax(0, 1fr))' : 'minmax(0, 1fr)',
            gridAutoRows: '1px',
            gridAutoFlow: 'row dense',
            columnGap: spacing.lg,
            paddingInline,
        }}>
            {Children.toArray(children).map(child => (
                <MasonryItem key={child.key} gap={spacing.lg}>{child}</MasonryItem>
            ))}
        </div>
    );
}
